import React, {useState, useMemo, useCallback} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import Separator from '../../../../../components/Simple/Sepperator';
import PlanEditorInnerContainer from '../../../../../components/Editor/PlanEditorInnerContainer';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import {useTranslation} from 'react-i18next';
import {useBoolean} from '../../../../../utils/hooks';
import SMCTable from '../../../../../components/core/SMCTable/SMCTable';
import ButtonContainer from '../../../../../components/Simple/ButtonContainer';
import TPExceptionsWhitelistUrlEditorModal from '../../../../PopupPages/TPExceptionsWhitelistUrlEditorModal';
import SMCButton from '../../../../../components/Simple/SMCButton';
import SMCIcon from '../../../../../components/Simple/SMCIcon';
import SMCNote from '../../../../../components/Simple/SMCNote';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.threatPreventionExceptions;
const pageId = pageSchema.pageId;

function BaseThreatPreventionExceptions({
    pageData,
    setObjectData,
    pageDisabled,
    baseObjectName,
    objectType
}) {
    const { t } = useTranslation();
    const managedInSmp   = pageData[pageSchema.manageInSmp];
    const whitelistUrls = pageData[pageSchema.whitelistUrls];

    const setPageData = useCallback((paramName, newValue) => {
        if (!pageDisabled) {
            setObjectData([pageId, paramName], newValue);
        }
    }, [pageDisabled, setObjectData]);

    const [isWhitelistUrlEditorOpen, openWhitelistUrlEditor, closeWhitelistUrlEditor] = useBoolean(false);
    const [selectedObjects, setSelectedObjects] = useState([]);

    // Editor objects
    const [editObject, setEditObject] = useState(null);
    const [editObjectIndex, setEditObjectIndex] = useState(null);

    const deleteSelectedWhitelistUrls = useCallback(() => {
        if (whitelistUrls && selectedObjects.length > 0) {
           const newWhitelistUrls = whitelistUrls.filter((elem, i) => {
                const normalizedIndex = i + 1;
                return selectedObjects.indexOf(normalizedIndex) === -1;
                 });
            setSelectedObjects([]);
            setPageData(pageSchema.whitelistUrls, newWhitelistUrls);
        }
    }, [selectedObjects, setPageData, whitelistUrls]);

    const whitelistUrlEditorOnCancel = useCallback(() => {
        // Set edit object and edit object key to null, to signify 
        setEditObject(null);
        setEditObjectIndex(null);
        closeWhitelistUrlEditor();
    }, [closeWhitelistUrlEditor]);

    const newWhitelistUrlCallback = useCallback((newWhitelistUrlsObject) => {
            const whitelistUrlsCopy = whitelistUrls ? [...whitelistUrls] : [];
            whitelistUrlsCopy.push({
                [pageSchema.whiteListUrlSchema.value]: newWhitelistUrlsObject.value,
            });
            setPageData(pageSchema.whitelistUrls, whitelistUrlsCopy);
            closeWhitelistUrlEditor();
    }, [closeWhitelistUrlEditor, setPageData, whitelistUrls]);

    const editWhitelistUrlCallback = useCallback((newWhitelistUrlsObject) => {
        const whitelistUrlsCopy = whitelistUrls ? [...whitelistUrls] : [];
        whitelistUrlsCopy[editObjectIndex] = {
            [pageSchema.whiteListUrlSchema.value]: newWhitelistUrlsObject.value
        };
        setPageData(pageSchema.whitelistUrls, whitelistUrlsCopy);
        setEditObject(null);
        setEditObjectIndex(null);
        closeWhitelistUrlEditor();
    }, [closeWhitelistUrlEditor, editObjectIndex, setPageData, whitelistUrls]);


    const urlsTableColumns = useMemo(() => ([
       // { id: 'count',                                              header: '#',           width: 32,  align: 'center',  Cell: ({index}) => (index + 1)                                                                                                                                        },
        { id: 'edit',                                               header: '',            width: 22,                    Cell: ({index, object}) => ( <SMCIcon icon="edit" style={{display: 'block', marginLeft : 'auto', marginRight: 'auto'}} onClick={() => { setEditObject(object); setEditObjectIndex(index); openWhitelistUrlEditor(); }} />)    },
        { id: 'url', accessor: pageSchema.whiteListUrlSchema.value, header: 'URL',         width: 225                                                                                                                                                                                                                                                  },
    ]),[openWhitelistUrlEditor]);

    
    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={managedInSmp}
                onChange={checked => setPageData(pageSchema.manageInSmp, checked)}
                label={t("SMP.PLAN.MANAGE_IN_SMP")}
            />
            <Separator isMainSeparator/>
            <SMCNote>{t('SMP.PLAN.TP_EX.LINE1')}</SMCNote>
            <PlanEditorInnerContainer title={t('SMP.PLAN.TP_EX.WHITELIST_URLS')} managedInSmp={managedInSmp}>
                <ButtonContainer>
                    <SMCButton variant="ghost" disabled={pageDisabled || !managedInSmp} icon="new" onClick={openWhitelistUrlEditor}>{t('SMC_GEN_NEW')}</SMCButton>
                    <SMCButton variant="ghost" disabled={pageDisabled || !managedInSmp} icon="delete" onClick={deleteSelectedWhitelistUrls}>{t('SMC_GEN_DELETE')}</SMCButton>
                </ButtonContainer>
                <SMCTable
                    columns={urlsTableColumns}
                    hasCheckAll={true}
                    rowIdSelector={(obj, index) => index + 1}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={setSelectedObjects}
                    content={whitelistUrls}
                    width={500}
                    maxHeight={500}
                />
                <TPExceptionsWhitelistUrlEditorModal
                    isOpen={isWhitelistUrlEditorOpen}
                    onSuccess={editObject ? editWhitelistUrlCallback : newWhitelistUrlCallback}
                    onCancel={whitelistUrlEditorOnCancel}
                    defaultObject={editObject}
                    baseObjectName={baseObjectName}
                    objectType={objectType}
                    title={editObject ? t('SMP.PLAN.SEC_SOFT_BLADES.TP_EX_WIZ_EDIT_URL') : t('SMP.PLAN.SEC_SOFT_BLADES.TP_EX_WIZ_ADD_URL')}
                />
            </PlanEditorInnerContainer>
        </EditorLayout>
    );
}

export default BaseThreatPreventionExceptions;